﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_OPERATORS_SUCCESS, FINAL_OPERATORS_REQUEST, INITIAL_OPERATORS_REQUEST } from "@/redux/types/maintainers/operators.type";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import OperatorsTableHead from "./OperatorsTableHead";
import OperatorsTableBody from "./OperatorsTableBody";
import OperatorsForm from "./OperatorsForm/OperatorsForm";

const Operators = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers, estates_parents, estates, categories } = useSelector((state) => state.maintainers_operators);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);

	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_OPERATORS_REQUEST });
		api.get("/admin/operators", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_id, order, orderType, paginate, page]);

	const updateData = (response) => {
		const { operators, customers, estates_parents, estates, categories, permissions } = response || {};
		setPermissions(permissions || []);
		if (!operators?.data) {
			return;
		}
		dispatch({
			type: FETCH_OPERATORS_SUCCESS,
			payload: {
				operators: operators.data,
				last_page: operators.last_page,
				from: operators.from,
				to: operators.to,
				quantity: operators.total,
				customers: customers,
				estates_parents: estates_parents,
				estates: estates,
				categories: categories,
			},
		});
		setLoaded(true);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_OPERATORS_REQUEST });
		api.post("/admin/operators/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			if (Object.prototype.hasOwnProperty.call(response, "operators")) {
				toast(addToast, "Operador eliminado correctamente", "success");
				updateData(response);
			} else {
				dispatch({ type: FINAL_OPERATORS_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/operators/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Operadores"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Operador"}
				action={() => navigate("/configuracion/operadores/crear")}
				back_url={"/configuracion?section=users"}
			/>

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					<Col md={12} className={classes.main_col}>
						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<OperatorsTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<OperatorsTableBody handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<OperatorsForm
						customers={customers}
						estates_parents={estates_parents}
						estates={estates}
						categories={categories}
						confirmSave={confirmSave}
						permissions={permissions}
					/>
				</>
			)}
		</>
	);
};

export default Operators;
